/**
 * Словарь интерфейса (§12 спеки). Правило спеки: ни одной строки текста
 * в компонентах — всё берётся отсюда дословно.
 *
 * Файл общий для всех волн разработки: каждая волна добавляет свой раздел
 * и не правит чужие. Ключи повторяют нумерацию спеки (§12.1 … §12.8),
 * чтобы строку всегда можно было сверить с источником.
 */

// ───────────────────────────── склонение числительных ────────────────────────
/**
 * Русские формы по числу: plural(1, ['правка','правки','правок']) → 'правка'.
 * Формы: 1 — первая, 2–4 — вторая, 5+ и 11–14 — третья.
 */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return forms[2]
  if (last > 1 && last < 5) return forms[1]
  if (last === 1) return forms[0]
  return forms[2]
}

/** Число вместе со склонённым словом: '14 правок'. */
export function countOf(n: number, forms: readonly [string, string, string]): string {
  return `${n} ${plural(n, forms)}`
}

export const forms = {
  change: ['правка', 'правки', 'правок'] as const,
  brand: ['бренд', 'бренда', 'брендов'] as const,
  /** Предложный падеж: «в 1 бренде», «в 2 брендах». */
  brandIn: ['бренде', 'брендах', 'брендах'] as const,
  row: ['строка', 'строки', 'строк'] as const,
  conflict: ['конфликт', 'конфликта', 'конфликтов'] as const,
  category: ['категория', 'категории', 'категорий'] as const,
  changeSet: ['пакет', 'пакета', 'пакетов'] as const,
  symbol: ['символа', 'символов', 'символов'] as const,
}

export const texts = {
  /** §12.1 Кнопки и действия */
  action: {
    commit: (n: number) => `Подтвердить изменения (${n})`,
    commitFinal: (n: number) => `Подтвердить и опубликовать (${n})`,
    commitHint: 'После этого правки увидят все и в чат уйдёт одно сообщение.',
    commitBlocked: (n: number) => `Сначала решите ${countOf(n, forms.conflict)}`,
    commitWaiting: (n: number) => `Ждём ${countOf(n, forms.change)}`,
    draftCancelAll: 'Отменить весь черновик',
    draftShowMine: 'Показать только мои правки',
    draftShowAll: '← Показать все бренды',
    removeChange: 'убрать',
    undo: 'Вернуть',
    backToTable: 'Назад к таблице',
    openReview: 'Открыть ревью',
    refresh: 'Обновить',
    view: 'Посмотреть',
    retry: 'Повторить',
    close: 'Закрыть',
    cancel: 'Отмена',
    addBrand: '+ Бренд',
    addCategory: '+ Добавить',
    copyLink: '⧉ Ссылка на этот вид',
    copyBrandLink: '⧉ Ссылка на бренд',
    columns: '⚙ Колонки',
    filters: (n: number) => `⚗ Фильтры (${n})`,
    filtersReset: 'Сбросить фильтры',
    filtersClearAll: 'Очистить всё',
    saveView: '+ Сохранить вид',
    selectAllShown: (n: number) => `Выделить все ${n} показанных`,
    selectionClear: 'Снять',
    expandAll: 'Развернуть все',
    revert: 'Откатить пакет',
    exportCsv: '⬇ Выгрузить CSV',
    telegramTest: 'Отправить тестовое сообщение',
    telegramRetry: 'Отправить повторно',
    shortcuts: 'Справка и горячие клавиши',
  },

  /** §12.2 Заголовки экранов */
  screen: {
    login: { title: 'Управление брендами', subtitle: 'High Level' },
    brands: { title: 'Бренды' },
    draft: { title: 'Подтверждение изменений' },
    digest: { title: 'С вашего прошлого визита' },
    history: { title: 'История изменений' },
    changeSet: { title: (id: string | number) => `Пакет #${id}` },
    categories: { title: 'Справочник категорий' },
    enums: { title: 'Справочники значений' },
    fields: {
      title: 'Поля',
      subtitle:
        'Создание новых полей появится на этапе 2. Сейчас можно менять порядок и показ в таблице.',
    },
    users: { title: 'Пользователи и роли' },
    settings: { title: 'Настройки' },
  },

  /** Шапка приложения, §3 */
  topbar: {
    logo: 'HIGH LEVEL',
    logoSuffix: ' · Бренды',
    logoAriaLabel: 'HIGH LEVEL · Бренды — к таблице брендов',
    nav: {
      label: 'Разделы',
      brands: 'Бренды',
      history: 'История',
      catalog: 'Справочники',
      fields: 'Поля',
      settings: 'Настройки',
    },
    searchPlaceholder: 'Поиск бренда ( / )',
    searchLabel: 'Поиск бренда',
    skipToContent: 'К содержимому',
    draftChip: (n: number) => `Черновик · ${n}`,
    draftChipLabel: (n: number) => `Черновик: ${countOf(n, forms.change)}. Открыть список`,
    userMenu: 'Меню пользователя',
    density: { label: 'Плотность', normal: 'Обычная', compact: 'Компактная' },
    densityNormalHint: 'Обычная плотность строк',
    densityCompactHint: 'Компактная: на экран помещается в 2,5 раза больше брендов',
    theme: { label: 'Тема', system: 'Как в системе', light: 'Светлая тема', dark: 'Тёмная тема' },
    editMode: { label: 'Правка', enter: 'по Enter', sheets: 'как в Google Sheets' },
    myArea: 'Моя зона ответственности',
    myAreaSoon: 'Раздел профиля появится вместе с зонами ответственности',
    usersAndRoles: 'Пользователи и роли',
    build: (stamp: string) => `Сборка ${stamp} UTC`,
    updateReady: 'Вышла новая версия интерфейса',
    updateAction: 'Обновить',
    logout: 'Выйти',
  },

  /** §6.2 Поповер чипа черновика */
  draftPopover: {
    heading: (changes: number, brands: number, since: string | null) =>
      since
        ? `${countOf(changes, forms.change)} в ${countOf(brands, forms.brand)} · с ${since}`
        : `${countOf(changes, forms.change)} в ${countOf(brands, forms.brand)}`,
    more: (n: number) => `+ ещё ${countOf(n, forms.brand)}`,
    dropBrand: (name: string) => `Убрать правки бренда ${name}`,
    openReview: 'Открыть ревью',
    discard: 'Отменить черновик',
  },

  /** §11.1 / §11.3 Загрузка и ошибки загрузки */
  loading: {
    tableLabel: 'Загружаем бренды',
  },
  error: {
    brandsTitle: 'Не удалось загрузить бренды',
    unknown: 'Что-то сломалось на сервере. Мы записали ошибку, попробуйте ещё раз',
  },

  /** §11.4 Нет прав — экран раздела */
  denied: {
    title: 'Раздел недоступен',
    catalog: 'Нет права менять справочники. Доступно коммерческому директору и собственнику',
    fields: 'Нет права менять поля. Доступно коммерческому директору и собственнику',
    users: 'Нет права управлять пользователями. Доступно собственнику',
    settings: 'Нет права менять настройки. Доступно собственнику',
    history: 'Нет права смотреть историю. Доступно директорам и собственнику',
    generic: 'Доступ к этому разделу выдаёт собственник',
  },

  /** §12.7 Ошибки и отказы */
  fail: {
    loginBadCredentials: 'Не подходит логин или пароль',
    loginThrottled: 'Слишком много попыток. Попробуйте через 15 минут',
    sessionExpired: 'Сессия закончилась. Войдите снова — черновик на месте',
    rateLimited: 'Слишком много запросов. Подождите несколько секунд',
    server: 'Что-то сломалось на сервере. Мы записали ошибку, попробуйте ещё раз',
  },

  /** Вход и смена пароля */
  auth: {
    code: 'Код входа',
    codeHint: 'Шесть цифр из личного сообщения',
    byPassword: 'Войти по паролю',
    byCode: 'Войти по коду',
    email: 'E-mail',
    password: 'Пароль',
    submit: 'Войти',
    submitting: 'Входим…',
    changeTitle: 'Новый пароль',
    changeSubtitle: 'Задайте пароль, который знаете только вы',
    changeForcedSubtitle: 'Пароль выдан вам на один раз — задайте свой',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    repeatPassword: 'Повторите новый пароль',
    minLengthHint: 'Не короче 10 символов',
    changeSubmit: 'Сохранить пароль',
    changeSubmitting: 'Сохраняем…',
    changed: 'Пароль изменён',
    errTooShort: (min: number) => `Пароль должен быть не короче ${min} ${plural(min, forms.symbol)}`,
    errMismatch: 'Пароли не совпадают',
    errRequired: 'Поле обязательно к заполнению',
    errSameAsCurrent: 'Новый пароль совпадает с текущим',
  },

  /** Заготовки экранов следующих волн */
  stub: {
    body: 'Экран в разработке',
  },
} as const
